import React from 'react'
import { useRect } from '@reach/rect'
import { useRef, useCallback, useEffect, useContext } from 'sb-hooks'

import MenuContext from './MenuContext'


type MenuButtonProps = {
  children?: any
  id?: string
  className?: string
  role?: string
  onClick?: (event: React.MouseEvent) => void
  onKeyDown?: (event: React.KeyboardEvent) => void
  onMouseEnter?: (event: React.MouseEvent) => void
  onMouseLeave?: (event: React.MouseEvent) => void
  dataTestId?: string
}

const MenuButton: React.FunctionComponent<MenuButtonProps> = (props) => {
  const {
    id, className, children, role,
    onClick, onKeyDown, onMouseEnter, onMouseLeave,
    dataTestId,
    ...rest
  } = props

  const timeoutId = useRef()
  const context = useContext(MenuContext)
  const rectRef = useRef<HTMLButtonElement>()

  const {
    refs, state, setState,
    handleMenuClick, handleMenuMouseDown, handleMenuKeyDown,
    handleMenuMouseEnter, handleMenuMouseLeave,
    handleMenuBlur,
  } = context

  useEffect(() => () => {
    if (timeoutId.current) {
      clearTimeout(timeoutId.current)
    }
  }, [])

  const handleRef = useCallback((node) => {
    rectRef.current = node
    refs.current.button = node
  }, [])

  const handleChange = useCallback((buttonRect) => {
    setState({ buttonRect })
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useRect(rectRef, state.isOpen, handleChange)

  const handleClick = useCallback((event) => {
    handleMenuClick(event)

    if (typeof onClick === 'function') {
      onClick(event)
    }
  }, [ handleMenuClick, onClick ])

  const handleKeyDown = useCallback((event) => {
    handleMenuKeyDown(event)

    if (typeof onKeyDown === 'function') {
      onKeyDown(event)
    }
  }, [ handleMenuKeyDown, onKeyDown ])

  const handleMouseEnter = useCallback((event) => {
    handleMenuMouseEnter(event)

    if (typeof onMouseEnter === 'function') {
      onMouseEnter(event)
    }
  }, [ handleMenuMouseEnter, onMouseEnter ])

  const handleMouseLeave = useCallback((event) => {
    handleMenuMouseLeave(event)

    if (typeof onMouseLeave === 'function') {
      onMouseLeave(event)
    }
  }, [ handleMenuMouseLeave, onMouseLeave ])

  return (
    <button
      id={id || state.buttonId}
      className={className}
      role={role}
      aria-haspopup="menu"
      aria-expanded={state.isOpen}
      data-testid={dataTestId}
      type="button"
      ref={handleRef}
      onMouseDown={handleMenuMouseDown}
      onBlur={handleMenuBlur}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      {...rest}
    >
      {children}
    </button>
  )
}


export default React.memo(MenuButton)
